import { Outlet, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "@/components/footer/Footer";
// import { ChatIcon } from "./messages/ChatIcon";
// import { ChatProvider } from "@/context/ChatContext";
import { IntercomLauncherVisibilityProvider } from "@/context/IntercomLauncherVisibilityContext";
import { IntercomWidget } from "./intercom/IntercomWidget";
import {
  getNavigationItems,
  getAdminNavigationItems,
} from "./dashboard/navigationData";
import { useActivity } from "@/hooks/useActivity";
import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { NativeBackButtonHandler } from "@/components/native/NativeBackButtonHandler";
import NativeBottomNav from "@/components/native/NativeBottomNav";
import NativeMarketingRedirect from "@/components/native/NativeMarketingRedirect";
import { NativeSlideInPanel } from "@/components/native/NativeSlideInPanel";
import { dismissListingHeroTransition } from "@/lib/native/listingHeroTransition";
import { useTheme } from "@/context/ThemeContext";
import {
  configureNativeStatusBar,
  setNativeStatusBarOverlay,
} from "@/lib/native/capacitorBridge";
import {
  hasPendingListingsScrollRestore,
} from "@/lib/native/listingsScrollRestore";
import NativeAppShell from "@/components/native/NativeAppShell";
import { usePersonalRealtime } from "@/hooks/usePersonalRealtime";

import NativeFloatingProfile from "@/components/native/NativeFloatingProfile";
import NativeGuestFloatingSettings from "@/components/native/NativeGuestFloatingSettings";
import AppDownloadSheet from "@/components/index/AppDownloadSheet";
import { CookieConsentHost } from "@/components/cookie-consent/CookieConsentHost";
import { FeedbackPromptTip } from "@/components/dashboard/FeedbackPromptTip";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import {
  isAuctionsBrowseShellPath,
  isNativeListingDetailPath,
  normalizePath,
  shouldHideBottomNav,
  shouldHideNativeFloatingProfile,
  shouldShowGuestBottomNav,
  shouldUseNativeCompactTop,
} from "@/lib/native/nativeRoutes";

const Layout = () => {
  const { isNative: isNativeApp } = useNativePlatform();
  const { theme } = useTheme();
  const location = useLocation();
  const { user } = useAuth();
  // Send heartbeat to track user activity
  useActivity();
  usePersonalRealtime();

  // Scroll to top on route change unless returning to a listings page with saved scroll
  useEffect(() => {
    const stateY = (location.state as { restoreScrollY?: number } | null)
      ?.restoreScrollY;
    const pendingRestore =
      stateY != null ||
      hasPendingListingsScrollRestore(
        location.pathname,
        location.search,
      );
    if (!pendingRestore) {
      window.scrollTo(0, 0);
    }
    const path = normalizePath(location.pathname);
    if (isNativeApp && !isNativeListingDetailPath(path)) {
      dismissListingHeroTransition();
    }
  }, [location.pathname, location.search, location.state, isNativeApp]);
  const isFullWidthPage =
    isNativeApp ||
    location.pathname === "/kop-bil" ||
    location.pathname === "/salj-bilen" ||
    location.pathname === "/hur-det-fungerar" ||
    location.pathname === "/buy-car" ||
    location.pathname === "/sell-car" ||
    location.pathname === "/how-it-works";
  const path = normalizePath(location.pathname);
  const isGuestBrowse =
    isNativeApp &&
    !user &&
    shouldShowGuestBottomNav(path) &&
    path !== "/auth";
  const showBottomNav =
    isNativeApp && !shouldHideBottomNav(location.pathname);
  const isNativeAuthRoute =
    isNativeApp && path === "/auth";
  const isNativeListingDetail =
    isNativeApp && isNativeListingDetailPath(location.pathname);
  const showNativeHeader = isNativeApp && !user && path === "/auth";
  const showNativeFloatingProfile =
    isNativeApp &&
    Boolean(user) &&
    !shouldHideNativeFloatingProfile(location.pathname);
  const isNativeCompactTop =
    isNativeApp && shouldUseNativeCompactTop(location.pathname);
  /** Web /auctions browse: replace site Header with in-page Alla|För dig tabs */
  const hideWebHeaderForAuctionsBrowse =
    !isNativeApp && isAuctionsBrowseShellPath(location.pathname);

  useEffect(() => {
    const cls = "bilenia-native-guest-browse";
    if (isGuestBrowse) {
      document.documentElement.classList.add(cls);
    } else {
      document.documentElement.classList.remove(cls);
    }
    return () => document.documentElement.classList.remove(cls);
  }, [isGuestBrowse]);

  useEffect(() => {
    const cls = "bilenia-native-logged-in";
    if (showNativeFloatingProfile) {
      document.documentElement.classList.add(cls);
    } else {
      document.documentElement.classList.remove(cls);
    }
    return () => document.documentElement.classList.remove(cls);
  }, [showNativeFloatingProfile]);

  useEffect(() => {
    if (!isNativeApp) return;
    if (isNativeListingDetail) {
      void setNativeStatusBarOverlay(true);
      return () => {
        void setNativeStatusBarOverlay(false);
        void configureNativeStatusBar(theme === "dark");
      };
    }
  }, [isNativeApp, isNativeListingDetail, theme]);

  useEffect(() => {
    const cls = "bilenia-native-bottom-nav-visible";
    if (isNativeApp && showBottomNav) {
      document.documentElement.classList.add(cls);
    } else {
      document.documentElement.classList.remove(cls);
    }
    return () => document.documentElement.classList.remove(cls);
  }, [isNativeApp, showBottomNav]);

  const isEmbedPage = location.pathname === "/car-valuation-embed" || location.pathname === "/listings-embed";

  // Find matching navigation item for current path
  const findNavigationColor = (rawPath: string): string | undefined => {
    // /orders shares the merged "Mina annonser | Ordrar" hub (dashboard path /my-auctions)
    const path =
      rawPath === "/orders" || rawPath.startsWith("/orders/")
        ? "/my-auctions"
        : rawPath;
    const navigationItems = getNavigationItems();
    const adminNavigationItems = getAdminNavigationItems();

    // Check admin items first
    const adminItem = adminNavigationItems.find(
      (item) => path === item.path || path.startsWith(item.path + "/")
    );
    if (adminItem) return adminItem.color;

    // Check regular items
    const regularItem = navigationItems.find(
      (item) => path === item.path || path.startsWith(item.path + "/")
    );
    if (regularItem) return regularItem.color;

    return undefined;
  };

  const currentColor = findNavigationColor(location.pathname);

  // For embed page, render without header/footer/background
  if (isEmbedPage) {
    return (
      <div className="min-h-screen w-full bg-transparent">
        <NativeBackButtonHandler />
        <Outlet />
      </div>
    );
  }

  return (
    <IntercomLauncherVisibilityProvider>
      <NativeAppShell>
        <NativeBackButtonHandler />
        <div
          className={`min-h-screen flex flex-col${isNativeApp ? "" : " overflow-x-clip"}`}
        >
        <div
          className={`native-layout-inner w-full flex-1 flex flex-col ${
            !isFullWidthPage ? "max-w-5xl mx-auto" : ""
          }`}
        >
          {(!isNativeApp || showNativeHeader) &&
            !hideWebHeaderForAuctionsBrowse && <Header />}
          {isGuestBrowse && <NativeGuestFloatingSettings />}
          <main
            className={
              isNativeApp
                ? `native-layout-main flex-1${isNativeAuthRoute ? " native-auth-route" : ""}${isNativeListingDetail ? " native-listing-main" : ""}${isNativeCompactTop ? " native-layout-main-compact-top" : ""}${showNativeFloatingProfile ? " native-layout-main-no-header" : ""}${isGuestBrowse ? " native-layout-main-guest-browse" : ""}`
                : hideWebHeaderForAuctionsBrowse
                  ? "flex-1 pb-16 pt-3"
                  : !isFullWidthPage
                    ? "flex-1 pb-16 max-sm:pt-[calc(1rem+3.5rem+6px)] sm:pt-16"
                    : "flex-1"
            }
          >
            <NativeMarketingRedirect>
              <NativeSlideInPanel className="min-h-0 flex-1 flex flex-col items-stretch w-full min-w-0">
                <Outlet />
              </NativeSlideInPanel>
            </NativeMarketingRedirect>
          </main>
        </div>
        {!isNativeApp && (
          <div className={isFullWidthPage ? "pt-0" : "pt-24"}>
            <Footer />
          </div>
        )}
        {showBottomNav && <NativeBottomNav />}
        {showNativeFloatingProfile && <NativeFloatingProfile />}
        {!isNativeApp && <AppDownloadSheet />}
        <CookieConsentHost />
        <FeedbackPromptTip />
        <IntercomWidget />
      </div>
      </NativeAppShell>
    </IntercomLauncherVisibilityProvider>
  );
};

export default Layout;
